// =====================================================
// BULK CHECK-IN DIALOG
// Stage 3: Attendance Tracking
// Dialog for bulk check-in operations
// =====================================================

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, AlertCircle, Users } from 'lucide-react';
import { bulkCheckInParticipants, bulkCheckInAllPending } from '@/Services/eventAttendance.service';
import { useToast } from '@/hooks/use-toast';

// ===================
// TYPES
// ===================

interface BulkCheckInDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  selectedParticipantIds?: string[];
  mode: 'selected' | 'all-pending';
  onComplete?: () => void;
}

// ===================
// COMPONENT
// ===================

export function BulkCheckInDialog({
  open,
  onOpenChange,
  eventId,
  selectedParticipantIds = [],
  mode,
  onComplete,
}: BulkCheckInDialogProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<{
    success: number;
    failed: number;
    errors: string[];
  } | null>(null);
  const { toast } = useToast();

  const participantCount = mode === 'selected' ? selectedParticipantIds.length : 'todos los pendientes';

  const handleConfirm = async () => {
    setIsProcessing(true);
    setResult(null);

    try {
      let checkInResult;

      if (mode === 'selected') {
        checkInResult = await bulkCheckInParticipants({
          participant_ids: selectedParticipantIds,
        });
      } else {
        checkInResult = await bulkCheckInAllPending(eventId);
      }

      setResult({
        success: checkInResult.success,
        failed: checkInResult.failed,
        errors: Array.isArray(checkInResult.errors) ? checkInResult.errors : [],
      });

      if (checkInResult.success > 0) {
        toast({
          title: 'Registro completado',
          description: `${checkInResult.success} participante${checkInResult.success !== 1 ? 's' : ''} marcado${checkInResult.success !== 1 ? 's' : ''} como presente`,
        });
      }

      if (checkInResult.failed > 0) {
        toast({
          title: 'Errores detectados',
          description: `${checkInResult.failed} participante${checkInResult.failed !== 1 ? 's' : ''} no pudieron ser registrados`,
          variant: 'destructive',
        });
      }

      onComplete?.();
    } catch (error) {
      toast({
        title: 'Error en registro masivo',
        description: error instanceof Error ? error.message : 'Error desconocido',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="size-5" />
            Registro Masivo de Asistencia
          </DialogTitle>
          <DialogDescription>
            {mode === 'selected'
              ? `Marcar ${participantCount} participante${selectedParticipantIds.length !== 1 ? 's' : ''} seleccionado${selectedParticipantIds.length !== 1 ? 's' : ''} como presente`
              : 'Marcar todos los participantes pendientes como presentes'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!result && (
            <Alert>
              <CheckCircle2 className="size-4" />
              <AlertDescription>
                {mode === 'selected'
                  ? `Se registrará la asistencia de ${participantCount} participante${selectedParticipantIds.length !== 1 ? 's' : ''}.`
                  : 'Se registrará la asistencia de todos los participantes con estado "Pendiente".'}
                <br />
                Esta acción no se puede deshacer automáticamente.
              </AlertDescription>
            </Alert>
          )}

          {result && (
            <div className="space-y-3">
              {result.success > 0 && (
                <Alert>
                  <CheckCircle2 className="size-4" />
                  <AlertDescription>
                    <strong>{result.success}</strong> participante{result.success !== 1 ? 's' : ''}{' '}
                    registrado{result.success !== 1 ? 's' : ''} exitosamente
                  </AlertDescription>
                </Alert>
              )}

              {result.failed > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="size-4" />
                  <AlertDescription>
                    <strong>{result.failed}</strong> participante{result.failed !== 1 ? 's' : ''} no pudieron
                    ser registrados
                    {result.errors.length > 0 && (
                      <ul className="mt-2 list-inside list-disc text-xs">
                        {result.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isProcessing}>
                Cancelar
              </Button>
              <Button onClick={handleConfirm} disabled={isProcessing}>
                {isProcessing ? 'Procesando...' : 'Confirmar Registro'}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>Cerrar</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
